// Fiche d'une cargaison (D2) : statut et cycle de la page 23, action principale selon le statut (fin de
// déchargement avec quantité réceptionnée, statut suivant), report motivé, position maritime saisie à la main
// avec son horodatage et son âge, contrat d'interface AIS « non raccordé », documents, événements tracés.

import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formaterNombre } from '@carburanttrack/shared';
import { STATUTS_CARGAISON } from '@carburanttrack/shared/matiere';
import { api, messageErreur } from '../../api/client';
import type { DocumentListe } from '../../api/types';
import { Message, MessageErreur, Squelette } from '../../composants/Etats';
import { date, dateHeure } from '../../composants/format';
import { decimalSaisi, localVersIso, maintenantLocal, quantite } from './composants';
import type { FicheCargaison } from './types';

const ORDRE = STATUTS_CARGAISON.filter((s) => s !== 'annule');

export function PageCargaison() {
  const { id } = useParams();
  const client = useQueryClient();
  const fiche = useQuery({ queryKey: ['approvisionnements', 'cargaison', id], queryFn: async () => (await api<FicheCargaison>(`/api/v1/approvisionnements/cargaisons/${id}`)).donnees });
  const [panneau, setPanneau] = useState<'dechargement' | 'statut' | 'report' | 'position' | 'document' | null>(null);
  const rafraichir = () => {
    void client.invalidateQueries({ queryKey: ['approvisionnements'] });
    void client.invalidateQueries({ queryKey: ['stocks'] });
    void client.invalidateQueries({ queryKey: ['alertes'] });
  };
  if (fiche.isLoading) return <Squelette lignes={8} libelle="Chargement de la cargaison" />;
  if (fiche.error) return <MessageErreur erreur={fiche.error} titre="Cargaison indisponible" />;
  const f = fiche.data!;
  const c = f.cargaison;
  const enCours = !['reception_terminee', 'controle_en_cours', 'cloture', 'annule'].includes(c.statutCargaison);
  const rangCourant = ORDRE.indexOf(c.statutCargaison as (typeof ORDRE)[number]);
  const suivants = c.transitionsPossibles.filter((s) => s !== 'reception_terminee');

  return (
    <div className="page-cargaison">
      <div className="page-tete">
        <div>
          <span className="contexte">
            {c.reference}, {c.receptionNom}, {c.proprietaireNom}
          </span>
          <h1>
            {c.navire}, {c.produitNom}
          </h1>
          <p>
            <span className={`etiquette${c.joursReport > 0 && enCours ? ' ambre' : ''}`}>{c.statutLibelle}</span> {quantite(c.quantiteAnnoncee, c.unite)} annoncés, port de {c.port}
          </p>
        </div>
        {f.droits.modifier && (
          <div className="actions">
            {c.statutCargaison === 'en_dechargement' && f.droits.decharger && (
              <button type="button" className="bouton primaire" onClick={() => setPanneau('dechargement')}>
                Terminer le déchargement
              </button>
            )}
            {suivants.length > 0 && (
              <button type="button" className={`bouton${c.statutCargaison === 'en_dechargement' ? '' : ' primaire'}`} onClick={() => setPanneau('statut')}>
                Changer de statut
              </button>
            )}
            {enCours && (
              <button type="button" className="bouton" onClick={() => setPanneau('report')}>
                Modifier la date d’arrivée
              </button>
            )}
            {enCours && (
              <button type="button" className="bouton" onClick={() => setPanneau('position')}>
                Saisir une position
              </button>
            )}
            <button type="button" className="bouton discret" onClick={() => setPanneau('document')}>
              Rattacher un document
            </button>
          </div>
        )}
      </div>

      {panneau === 'dechargement' && <FormulaireDechargement fiche={f} fermer={() => setPanneau(null)} rafraichir={rafraichir} />}
      {panneau === 'statut' && <FormulaireStatut id={c.id} suivants={suivants} libelles={f.libellesStatuts} fermer={() => setPanneau(null)} rafraichir={rafraichir} />}
      {panneau === 'report' && <FormulaireReport id={c.id} actuelle={c.dateActualisee} fermer={() => setPanneau(null)} rafraichir={rafraichir} />}
      {panneau === 'position' && <FormulairePosition id={c.id} fermer={() => setPanneau(null)} rafraichir={rafraichir} />}
      {panneau === 'document' && <FormulaireDocument id={c.id} installationId={c.installationReceptionId} fermer={() => setPanneau(null)} rafraichir={rafraichir} />}

      {c.joursReport > 0 && enCours && (
        <section className="a-faire" aria-label="Report">
          <div>
            <span className="sur-titre">Report</span>
            <h2>
              Arrivée reportée de {c.joursReport} jour{c.joursReport > 1 ? 's' : ''} : actualisée au {date(c.dateActualisee)}
            </h2>
            <p>Prévue initialement le {date(c.datePrevue)}. Le report est tracé et alimente l’alerte « cargaison reportée ».</p>
          </div>
        </section>
      )}

      <ol className="etapes-cycle" aria-label="Cycle de statuts">
        {ORDRE.map((s, i) => (
          <li key={s} className={i <= rangCourant ? 'atteinte' : undefined} aria-current={s === c.statutCargaison ? 'step' : undefined}>
            {f.libellesStatuts[s] ?? s}
          </li>
        ))}
        {c.statutCargaison === 'annule' && <li aria-current="step">{f.libellesStatuts.annule}</li>}
      </ol>

      <div className="deux-colonnes">
        <div className="principal">
          <section className="carte-bloc" aria-labelledby="titre-quantites">
            <h2 id="titre-quantites">Quantités et dates</h2>
            <dl className="paires">
              <div>
                <dt>Quantité annoncée</dt>
                <dd>{quantite(c.quantiteAnnoncee, c.unite)}</dd>
              </div>
              <div>
                <dt>Quantité réceptionnée</dt>
                <dd>{c.quantiteReceptionnee ? quantite(c.quantiteReceptionnee, c.unite) : 'indisponible : réception non terminée'}</dd>
              </div>
              <div>
                <dt>Écart à l’annoncé</dt>
                <dd>
                  {c.ecart !== null ? `${quantite(c.ecart, c.unite)} (${formaterNombre(String((Number(c.ecart) / Number(c.quantiteAnnoncee)) * 100), 2)} %)` : 'indisponible'}
                  {c.ecartMotif ? `, ${c.ecartMotif}` : ''}
                </dd>
              </div>
              <div>
                <dt>Date prévue</dt>
                <dd>{dateHeure(c.datePrevue)}</dd>
              </div>
              <div>
                <dt>Date actualisée</dt>
                <dd>{dateHeure(c.dateActualisee)}</dd>
              </div>
              <div>
                <dt>Déchargement</dt>
                <dd>{c.debutDechargement ? `du ${dateHeure(c.debutDechargement)}${c.finDechargement ? ` au ${dateHeure(c.finDechargement)}` : ', en cours'}` : 'indisponible : non commencé'}</dd>
              </div>
              <div>
                <dt>Programmation liée</dt>
                <dd>{f.programme ? `${f.programme.reference}, version ${f.programme.version}` : 'aucune'}</dd>
              </div>
            </dl>
          </section>
          <section className="carte-bloc" aria-labelledby="titre-evenements">
            <h2 id="titre-evenements">Événements tracés</h2>
            {f.evenements.length === 0 ? (
              <p className="attenue">Aucun événement.</p>
            ) : (
              <ol className="chronologie">
                {f.evenements.map((e) => (
                  <li key={e.id}>
                    {libelleEvenement(e.type, e.apres, f.libellesStatuts)}
                    <small>
                      {dateHeure(e.horodatageEvenement)}
                      {e.auteur ? `, ${e.auteur.prenom} ${e.auteur.nom}` : ''}
                      {e.motif ? `, motif : ${e.motif}` : ''}
                    </small>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>
        <aside className="lateral">
          <section className="carte-bloc" aria-labelledby="titre-position">
            <h2 id="titre-position">Position du navire</h2>
            {c.dernierePosition ? (
              <dl className="paires">
                <div>
                  <dt>Coordonnées</dt>
                  <dd>
                    latitude {c.dernierePosition.latitude.toFixed(3).replace('.', ',')} ; longitude {c.dernierePosition.longitude.toFixed(3).replace('.', ',')}
                  </dd>
                </div>
                <div>
                  <dt>Saisie</dt>
                  <dd>
                    {dateHeure(c.dernierePosition.horodatage)}, {c.agePosition}
                  </dd>
                </div>
                <div>
                  <dt>Source</dt>
                  <dd>Saisie manuelle{c.dernierePosition.commentaire ? `, ${c.dernierePosition.commentaire}` : ''}</dd>
                </div>
              </dl>
            ) : (
              <p className="attenue">Aucune position saisie.</p>
            )}
          </section>
          <section className="carte-bloc" aria-labelledby="titre-ais">
            <h2 id="titre-ais">{f.connecteurAis.nom}</h2>
            <ul className="liste-connecteurs">
              <li className="non-raccorde">
                <span>
                  <b>Connecteur AIS</b>
                  <small>{f.connecteurAis.motif}</small>
                </span>
                <span className="etat-connecteur">Non raccordé</span>
              </li>
            </ul>
            <dl className="paires">
              <div>
                <dt>Nature</dt>
                <dd>{f.connecteurAis.nature}</dd>
              </div>
              <div>
                <dt>Fréquence</dt>
                <dd>{f.connecteurAis.frequence}</dd>
              </div>
              <div>
                <dt>En cas d’indisponibilité</dt>
                <dd>{f.connecteurAis.indisponibilite}</dd>
              </div>
            </dl>
          </section>
          <section className="carte-bloc" aria-labelledby="titre-documents-cargaison">
            <h2 id="titre-documents-cargaison">Documents</h2>
            {f.documents.length === 0 ? (
              <p className="attenue">Aucun document rattaché.</p>
            ) : (
              <ul className="liste-lignes">
                {f.documents.map((d) => (
                  <li key={d.id}>
                    <Link to={`/documents/${d.id}`}>
                      <span className="pictogramme">DOC</span>
                      <span className="texte">
                        <b>{d.titre}</b>
                        <small>{dateHeure(d.creeLe)}</small>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

function libelleEvenement(type: string, apres: unknown, libelles: Record<string, string>): string {
  const a = (apres ?? {}) as Record<string, string>;
  if (type === 'creation') return 'Cargaison enregistrée';
  if (type === 'statut') return `Statut : ${libelles[a.statut ?? ''] ?? a.statut ?? 'indisponible'}`;
  if (type === 'date') return `Date d’arrivée actualisée au ${date(a.dateActualisee)}`;
  if (type === 'position') return 'Position saisie';
  if (type === 'dechargement') return `Fin du déchargement : ${a.quantiteReceptionnee ? `${formaterNombre(a.quantiteReceptionnee)} réceptionnés` : 'quantité indisponible'}`;
  if (type === 'document') return 'Document rattaché';
  return type;
}

function FormulaireDechargement({ fiche, fermer, rafraichir }: { fiche: FicheCargaison; fermer: () => void; rafraichir: () => void }) {
  const c = fiche.cargaison;
  const [reservoirId, setReservoirId] = useState(fiche.reservoirsReception[0]?.id ?? '');
  const [q, setQ] = useState('');
  const [motif, setMotif] = useState('');
  const [quand, setQuand] = useState(maintenantLocal());
  const envoi = useMutation({
    mutationFn: async () => (await api<{ dansTolerance: boolean }>(`/api/v1/approvisionnements/cargaisons/${c.id}/dechargement`, { corps: { reservoirId, quantiteReceptionnee: decimalSaisi(q), ecartMotif: motif.trim() || null, horodatage: localVersIso(quand) } })).donnees,
    onSuccess: rafraichir,
  });
  return (
    <form
      className="panneau-saisie"
      onSubmit={(e) => {
        e.preventDefault();
        envoi.mutate();
      }}
    >
      <h2>Terminer le déchargement</h2>
      <p className="attenue">La quantité réceptionnée entre au dépôt selon le circuit de validation ; au-delà de la tolérance, l’écart doit être documenté.</p>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="d-reservoir">Réservoir de réception</label>
          <select id="d-reservoir" value={reservoirId} onChange={(e) => setReservoirId(e.target.value)}>
            {fiche.reservoirsReception.map((r) => (
              <option key={r.id} value={r.id}>
                {r.numero}, capacité {quantite(r.capaciteNominale, r.unite)}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="d-quantite">Quantité réceptionnée ({c.unite})</label>
          <input id="d-quantite" inputMode="decimal" value={q} onChange={(e) => setQ(e.target.value)} required />
          <span className="aide">Annoncée : {quantite(c.quantiteAnnoncee, c.unite)}</span>
        </div>
        <div className="champ">
          <label htmlFor="d-quand">Fin du déchargement</label>
          <input id="d-quand" type="datetime-local" value={quand} onChange={(e) => setQuand(e.target.value)} required />
        </div>
      </div>
      <div className="champ">
        <label htmlFor="d-motif">Motif de l’écart (obligatoire au-delà de la tolérance)</label>
        <input id="d-motif" value={motif} onChange={(e) => setMotif(e.target.value)} maxLength={1000} />
      </div>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {envoi.data && <Message type={envoi.data.dansTolerance ? 'succes' : 'avertissement'}>{envoi.data.dansTolerance ? 'Réception enregistrée, écart dans la tolérance.' : 'Réception enregistrée ; écart au-delà de la tolérance documenté et anomalie ouverte.'}</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire" disabled={envoi.isPending || !reservoirId}>
          Enregistrer la réception
        </button>
        <button type="button" className="bouton" onClick={fermer}>
          Fermer
        </button>
      </div>
    </form>
  );
}

function FormulaireStatut({ id, suivants, libelles, fermer, rafraichir }: { id: string; suivants: string[]; libelles: Record<string, string>; fermer: () => void; rafraichir: () => void }) {
  const [statut, setStatut] = useState(suivants[0] ?? '');
  const [motif, setMotif] = useState('');
  const envoi = useMutation({ mutationFn: async () => api(`/api/v1/approvisionnements/cargaisons/${id}/statut`, { corps: { statut, motif: motif.trim() || null } }), onSuccess: rafraichir });
  return (
    <form
      className="panneau-saisie"
      onSubmit={(e) => {
        e.preventDefault();
        envoi.mutate();
      }}
    >
      <h2>Changer de statut</h2>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="s-statut">Nouveau statut</label>
          <select id="s-statut" value={statut} onChange={(e) => setStatut(e.target.value)}>
            {suivants.map((s) => (
              <option key={s} value={s}>
                {libelles[s] ?? s}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="s-motif">Motif{statut === 'annule' ? ' (obligatoire)' : ', facultatif'}</label>
          <input id="s-motif" value={motif} onChange={(e) => setMotif(e.target.value)} required={statut === 'annule'} />
        </div>
      </div>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {envoi.isSuccess && <Message type="succes">Statut enregistré et tracé.</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire" disabled={envoi.isPending || !statut}>
          Enregistrer
        </button>
        <button type="button" className="bouton" onClick={fermer}>
          Fermer
        </button>
      </div>
    </form>
  );
}

function FormulaireReport({ id, actuelle, fermer, rafraichir }: { id: string; actuelle: string; fermer: () => void; rafraichir: () => void }) {
  const [jour, setJour] = useState(actuelle.slice(0, 10));
  const [motif, setMotif] = useState('');
  const envoi = useMutation({ mutationFn: async () => api(`/api/v1/approvisionnements/cargaisons/${id}/report`, { corps: { dateActualisee: `${jour}T08:00:00+01:00`, motif } }), onSuccess: rafraichir });
  return (
    <form
      className="panneau-saisie"
      onSubmit={(e) => {
        e.preventDefault();
        envoi.mutate();
      }}
    >
      <h2>Modifier la date d’arrivée</h2>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="r-date">Date actualisée</label>
          <input id="r-date" type="date" value={jour} onChange={(e) => setJour(e.target.value)} required />
        </div>
        <div className="champ">
          <label htmlFor="r-motif">Motif (obligatoire)</label>
          <input id="r-motif" value={motif} onChange={(e) => setMotif(e.target.value)} required minLength={3} />
        </div>
      </div>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {envoi.isSuccess && <Message type="succes">Date actualisée enregistrée ; la programmation liée est mise à jour.</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire" disabled={envoi.isPending}>
          Enregistrer
        </button>
        <button type="button" className="bouton" onClick={fermer}>
          Fermer
        </button>
      </div>
    </form>
  );
}

function FormulairePosition({ id, fermer, rafraichir }: { id: string; fermer: () => void; rafraichir: () => void }) {
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [quand, setQuand] = useState(maintenantLocal());
  const [commentaire, setCommentaire] = useState('');
  const envoi = useMutation({
    mutationFn: async () => api(`/api/v1/approvisionnements/cargaisons/${id}/position`, { corps: { latitude: Number(decimalSaisi(lat)), longitude: Number(decimalSaisi(lon)), horodatage: localVersIso(quand), commentaire: commentaire.trim() || null } }),
    onSuccess: rafraichir,
  });
  return (
    <form
      className="panneau-saisie"
      onSubmit={(e) => {
        e.preventDefault();
        envoi.mutate();
      }}
    >
      <h2>Saisir une position</h2>
      <p className="attenue">Position communiquée par l’armateur ou le capitaine, conservée avec son horodatage ; aucune position n’est simulée.</p>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="po-lat">Latitude (degrés décimaux)</label>
          <input id="po-lat" inputMode="decimal" value={lat} onChange={(e) => setLat(e.target.value)} required />
        </div>
        <div className="champ">
          <label htmlFor="po-lon">Longitude (degrés décimaux)</label>
          <input id="po-lon" inputMode="decimal" value={lon} onChange={(e) => setLon(e.target.value)} required />
        </div>
        <div className="champ">
          <label htmlFor="po-quand">Horodatage de la position</label>
          <input id="po-quand" type="datetime-local" value={quand} onChange={(e) => setQuand(e.target.value)} required />
        </div>
      </div>
      <div className="champ">
        <label htmlFor="po-commentaire">Commentaire, facultatif</label>
        <input id="po-commentaire" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} maxLength={500} />
      </div>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {envoi.isSuccess && <Message type="succes">Position enregistrée.</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire" disabled={envoi.isPending}>
          Enregistrer la position
        </button>
        <button type="button" className="bouton" onClick={fermer}>
          Fermer
        </button>
      </div>
    </form>
  );
}

function FormulaireDocument({ id, installationId, fermer, rafraichir }: { id: string; installationId: string; fermer: () => void; rafraichir: () => void }) {
  const documents = useQuery({ queryKey: ['documents', 'installation', installationId], queryFn: async () => (await api<DocumentListe[]>(`/api/v1/documents?objetType=installation&objetId=${installationId}`)).donnees });
  const [documentId, setDocumentId] = useState('');
  const envoi = useMutation({ mutationFn: async () => api(`/api/v1/approvisionnements/cargaisons/${id}/documents`, { corps: { documentId } }), onSuccess: rafraichir });
  return (
    <form
      className="panneau-saisie"
      onSubmit={(e) => {
        e.preventDefault();
        envoi.mutate();
      }}
    >
      <h2>Rattacher un document</h2>
      <p className="attenue">
        Le document est d’abord déposé dans les <Link to={`/documents?objetId=${installationId}`}>documents de l’installation de réception</Link> (droits hérités), puis rattaché ici.
      </p>
      {documents.data && documents.data.length === 0 && <p className="attenue">Aucun document déposé sur l’installation de réception.</p>}
      {documents.data && documents.data.length > 0 && (
        <div className="champ">
          <label htmlFor="doc-choix">Document</label>
          <select id="doc-choix" value={documentId} onChange={(e) => setDocumentId(e.target.value)} required>
            <option value="">Choisir</option>
            {documents.data.map((d) => (
              <option key={d.id} value={d.id}>
                {d.titre}
              </option>
            ))}
          </select>
        </div>
      )}
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {envoi.isSuccess && <Message type="succes">Document rattaché.</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire" disabled={envoi.isPending || !documentId}>
          Rattacher
        </button>
        <button type="button" className="bouton" onClick={fermer}>
          Fermer
        </button>
      </div>
    </form>
  );
}
